import type { JSX } from 'react';
import type { VerificationStatus } from '@/features/verifications/types/verification.types';
import type { InspectionVerification } from '../types/inspection.types';

interface InspectionOutcomeProps {
  verification: InspectionVerification;
}

/** Only an inspected verification has an outcome to report. */
const OUTCOME_VERBS: Partial<Record<VerificationStatus, string>> = {
  VERIFIED: 'Verified',
  RETURNED: 'Returned',
};

function outcomeLine(verb: string, verification: InspectionVerification): string {
  const { inspectedBy, inspectedAt } = verification;
  const inspector = inspectedBy ? ` by ${inspectedBy}` : '';
  const date = inspectedAt ? ` on ${new Date(inspectedAt).toLocaleDateString()}` : '';
  return `${verb}${inspector}${date}`;
}

/**
 * What the inspection decided: who closed the verification and when, plus the
 * comment the super admin sent back with a returned one.
 */
export function InspectionOutcome({ verification }: InspectionOutcomeProps): JSX.Element | null {
  const verb = OUTCOME_VERBS[verification.status];

  if (!verb) {
    return null;
  }

  return (
    <div className="space-y-1 border-t pt-3">
      <p className="text-sm text-muted-foreground">{outcomeLine(verb, verification)}</p>
      {verification.inspectionComment && (
        <p className="text-sm italic">{verification.inspectionComment}</p>
      )}
    </div>
  );
}
